import { S, toast, comfort } from "./store.ts";
import { api } from "./api.ts";
let AC: AudioContext | null = null;
export const sound = { on: localStorage.getItem("numi.sound") !== "off" };
export function setSound(on: boolean) { sound.on = on; try { localStorage.setItem("numi.sound", on ? "on" : "off"); } catch {} if (!on) try { speechSynthesis.cancel(); } catch {} }

let MASTER: GainNode | null = null, DELAY: DelayNode | null = null;
function ctx() {
  AC = AC || new (window.AudioContext || (window as any).webkitAudioContext)();
  if (AC.state === "suspended") AC.resume();
  if (!MASTER) {
    // master bus: gentle compressor (never harsh/loud) + a very short soft echo for sparkle
    const comp = AC.createDynamicsCompressor(); comp.threshold.value = -18; comp.ratio.value = 4;
    MASTER = AC.createGain(); MASTER.gain.value = 0.9; MASTER.connect(comp); comp.connect(AC.destination);
    DELAY = AC.createDelay(0.5); DELAY.delayTime.value = 0.13;
    const fb = AC.createGain(); fb.gain.value = 0.25; const wet = AC.createGain(); wet.gain.value = 0.3;
    DELAY.connect(fb); fb.connect(DELAY); DELAY.connect(wet); wet.connect(comp);
  }
  return AC;
}
/** [freq, start, dur, type?, vol?]. Each note is a soft mallet: fundamental plus a quiet octave and fifth, smooth envelope. */
function tone(seq: [number, number, number, OscillatorType?, number?][]) {
  if (!sound.on) return;
  try {
    const ac = ctx(); const t0 = ac.currentTime;
    for (const [f, st, d, type, vol] of seq) {
      const v = (vol || 0.2) * (comfort().softSound ? 0.35 : 1);
      for (const [mul, amp, ty] of [[1, 1, type || "sine"], [2, 0.25, "sine"], [3, 0.08, "sine"]] as [number, number, OscillatorType][]) {
        const o = ac.createOscillator(), g = ac.createGain();
        o.type = ty; o.frequency.value = f * mul;
        const dd = d / (mul === 1 ? 1 : mul);
        g.gain.setValueAtTime(0.0001, t0 + st); g.gain.exponentialRampToValueAtTime(v * amp, t0 + st + 0.012); g.gain.exponentialRampToValueAtTime(0.0001, t0 + st + dd);
        o.connect(g); g.connect(MASTER!); if (DELAY && mul === 1) g.connect(DELAY);
        o.start(t0 + st); o.stop(t0 + st + dd + 0.03);
      }
    }
  } catch {}
}
const buzz = (ms: number) => { if (sound.on && !comfort().softSound) try { navigator.vibrate?.(ms); } catch {} };
const PENTA = [523, 587, 659, 784, 880, 1047, 1175, 1319]; // C major pentatonic: any run of notes sounds happy
export const SFX = {
  ok: () => { tone([[660, 0, 0.16], [880, 0.1, 0.34]]); buzz(15); },
  no: () => tone([[330, 0, 0.22, "triangle", 0.1], [262, 0.14, 0.3, "triangle", 0.08]]), // soft two-note "hmm", never a buzz
  pop: (n: number) => tone([[PENTA[Math.max(0, Math.min(n | 0, 7))], 0, 0.14, "sine", 0.16]]),
  up: () => { tone([[523, 0, 0.16], [659, 0.11, 0.16], [784, 0.22, 0.16], [1047, 0.33, 0.5]]); buzz(30); },
  tap: () => tone([[700, 0, 0.05, "triangle", 0.08]]),
  /** streak of first-try answers: the run climbs the scale */
  combo: (n: number) => { const i = Math.min(Math.max(n - 2, 0), 5); tone([[PENTA[i], 0, 0.14], [PENTA[i + 1], 0.09, 0.14], [PENTA[Math.min(i + 3, 7)], 0.18, 0.4]]); buzz(20); },
  star: () => tone([[1175, 0, 0.12, "sine", 0.14], [1568, 0.07, 0.35, "sine", 0.14]]),
  sticker: () => { tone([[523, 0, 0.14], [659, 0.1, 0.14], [784, 0.2, 0.14], [1047, 0.3, 0.14], [1319, 0.4, 0.14], [1568, 0.5, 0.6]]); buzz(60); },
};

export const canSpeak = (() => { try { return "speechSynthesis" in window; } catch { return false; } })();
const BOARD_VOICE: Record<string, string> = { cbse: "en-IN", icse: "en-IN", uae: "en-GB", gen: "en-US" };
const LANG_VOICE: Record<string, string> = { Hindi: "hi-IN", Arabic: "ar-SA", Hinglish: "en-IN" };
const englishSpoken = (t: string) => t.replace(/×/g, " times ").replace(/÷/g, " divided by ").replace(/−/g, " minus ")
  .replace(/☐/g, " box ").replace(/\+/g, " plus ").replace(/=/g, " equals ")
  .replace(/₹\s?(\d+(?:\.\d+)?)/g, "$1 rupees").replace(/AED\s?(\d+(?:\.\d+)?)/g, "$1 dirhams").replace(/\$\s?(\d+(?:\.\d+)?)/g, "$1 dollars");
const cache = new Map<string, string>();
let seq = 0; const warned = new Set<string>();
let voices: SpeechSynthesisVoice[] = [];
if (canSpeak) { const load = () => { voices = speechSynthesis.getVoices(); }; load(); speechSynthesis.addEventListener?.("voiceschanged", load); }
// Prefer higher-quality voices when several match the language. Browsers that expose cloud/neural
// voices (Chrome's "Google …", Edge's "… Online (Natural)", Safari's "Enhanced"/"Premium") sound far
// less robotic than the device's built-in offline voice, but speechSynthesis lists them unordered.
const QUALITY_HINTS = /natural|neural|enhanced|premium|wavenet|studio/i;
const BRAND_HINTS = /google|microsoft/i;
// SpeechSynthesisVoice has no real gender field, so "warm"/"bright" are a best-effort match on the
// voice's own name — most platforms expose this in the name (e.g. "Google UK English Female",
// "Microsoft Ravi", "Samantha"). A miss just falls back to the top quality-ranked voice, never an error.
const FEMININE_HINTS = /female|woman|girl|samantha|aria|zira|susan|karen|moira|tessa|fiona|veena|lekha|salli|ivy|joanna|kendra|kimberly|amy|emma|salma/i;
const MASCULINE_HINTS = /male(?!\w)|man|boy|daniel|alex|fred|ravi|rishi|george|arthur|oliver|james|guy|matthew|joey|brian/i;
export type GenderPref = "auto" | "warm" | "bright";
function rankVoice(v: SpeechSynthesisVoice, gender: GenderPref = "auto") {
  let score = 0;
  if (QUALITY_HINTS.test(v.name)) score += 3;
  if (BRAND_HINTS.test(v.name)) score += 1;
  if (!v.localService) score += 1; // network voice is usually higher quality than the offline fallback
  if (gender === "warm" && FEMININE_HINTS.test(v.name)) score += 5;
  if (gender === "bright" && MASCULINE_HINTS.test(v.name)) score += 5;
  return score;
}
function voiceFor(bcp: string, gender: GenderPref = "auto") {
  const l = bcp.toLowerCase(), base = l.slice(0, 2);
  const exact = voices.filter((v) => v.lang.toLowerCase().replace("_", "-") === l);
  const loose = exact.length ? exact : voices.filter((v) => v.lang.toLowerCase().startsWith(base));
  if (!loose.length) return undefined;
  return loose.slice().sort((a, b) => rankVoice(b, gender) - rankVoice(a, gender))[0];
}

// Split into short spoken clauses so pitch/pace can vary like real speech instead of one flat
// monotone utterance for the whole sentence.
function clauses(t: string) {
  return t.split(/(?<=[.!?,;:])\s+/).map((s) => s.trim()).filter(Boolean);
}

/**
 * Read text aloud in the child's language. English is spoken directly; Hindi, Arabic and Hinglish are
 * translated once (cached on the server for everyone) and spoken with the device's voice for that language.
 * `native: true` means the text is already in the child's language (e.g. Numi's AI replies).
 */
export async function speak(text: string, _board?: string, opts: { native?: boolean } = {}) {
  if (!sound.on || !canSpeak) return;
  const my = ++seq;
  const lang = S.child?.lang || "English", board = S.child?.board || "cbse";
  const gender: GenderPref = (S.child?.settings.voiceGender as GenderPref) || "auto";
  let t = String(text).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(); if (!t) return;
  let bcp = BOARD_VOICE[board] || "en-IN";
  if (lang !== "English") {
    const target = LANG_VOICE[lang] || bcp, orig = t;
    let translated = true;
    if (!opts.native && /[A-Za-z]{2}/.test(t)) {
      const k = lang + "|" + t; let tr = cache.get(k);
      if (!tr) {
        try { const r = await api("/translate", { body: { lang, texts: [t] } }); tr = r.texts[0]; if (r.translated) cache.set(k, tr!); else translated = false; }
        catch { translated = false; }
      }
      if (tr && translated) t = tr;
    }
    if (my !== seq) return;
    if (translated && (lang === "Hinglish" || voiceFor(target, gender))) bcp = target;
    else {
      // No translation or no voice for this language on the device: read the English text instead.
      if (translated && !opts.native && !warned.has(lang)) { warned.add(lang); toast(`This device has no ${lang} voice yet, so Numi will read in English. Add one in your device's language settings.`); }
      if (opts.native && !voiceFor(target, gender)) return; // an AI reply in Hindi/Arabic can't be read by an English voice
      t = orig;
    }
  }
  if (my !== seq) return;
  if (bcp.startsWith("en")) t = englishSpoken(t);
  try {
    speechSynthesis.cancel();
    const v = voiceFor(bcp, gender);
    const baseRate = bcp.startsWith("en") ? 0.9 : 0.85, basePitch = 1.15;
    const parts = clauses(t);
    parts.forEach((part, i) => {
      const u = new SpeechSynthesisUtterance(part);
      u.lang = bcp; if (v) u.voice = v;
      // Small per-clause variation stops the sentence reading as one flat monotone line, and a
      // short pause at the end of each clause (via trailing silence) mimics natural phrasing.
      const isQuestion = /\?\s*$/.test(part);
      u.rate = baseRate + (i % 2 ? 0.02 : -0.02);
      u.pitch = basePitch + (isQuestion ? 0.08 : 0) + (i % 3 === 0 ? 0.03 : -0.02);
      speechSynthesis.speak(u);
    });
  } catch {}
}

/** Play a short sample line in the given voice gender, independent of the saved setting —
 *  used by the "Preview" button in the voice-gender picker so a parent can hear before choosing. */
export function previewVoice(gender: GenderPref) {
  if (!canSpeak) return;
  try {
    speechSynthesis.cancel();
    const bcp = BOARD_VOICE[S.child?.board || "cbse"] || "en-IN";
    const v = voiceFor(bcp, gender);
    const u = new SpeechSynthesisUtterance("Hi! I'm Numi. Ready to learn some maths?");
    u.lang = bcp; if (v) u.voice = v; u.rate = 0.9; u.pitch = 1.15;
    speechSynthesis.speak(u);
  } catch {}
}
